import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import YoutubeVideo from "components/elements/youtubeVideo"
import Footer from "components/sections/footer"
import Header from "components/sections/headerV2"
import Seo from "components/sections/seo"
import SplitSectionV2 from "components/sections/splitSectionV2"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import {
  details,
  EpisodeCard,
  ListenLinks,
  TopicTags,
} from "views/podcastLibrary"
import "./podcast-episode.scss"

const SITE = "https://corticacare.com"

// 125 -> "2:05", 3725 -> "1:02:05"
const clock = total => {
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, "0")
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`
}

// Up to three other episodes, ones sharing a topic first, then the newest.
const moreEpisodes = (episode, others) => {
  const shared = others.filter(other =>
    (other.topics || []).some(topic => (episode.topics || []).includes(topic))
  )
  return [...shared, ...others.filter(other => !shared.includes(other))].slice(
    0,
    3
  )
}

const schemaFor = (episode, path) => ({
  "@context": "https://schema.org",
  "@type": "PodcastEpisode",
  datePublished: episode.publishedOn,
  description: episode.summary,
  episodeNumber: episode.episode || undefined,
  name: episode.title,
  partOfSeries: {
    "@type": "PodcastSeries",
    name: "Magnificent Minds",
    url: `${SITE}/podcast`,
  },
  timeRequired: episode.durationMinutes
    ? `PT${episode.durationMinutes}M`
    : undefined,
  url: `${SITE}${path}`,
  ...(episode.audioUrl && {
    associatedMedia: { "@type": "MediaObject", contentUrl: episode.audioUrl },
  }),
})

const PodcastEpisodeTemplate = ({
  data: { episode, footer, getStarted, header, more },
  pageContext: { transcript },
  path,
}) => {
  const [seek, setSeek] = useState(null)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [aboutLong, setAboutLong] = useState(false)
  const aboutRef = useRef(null)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const paragraphs = episode.descriptionParagraphs || []

  // "About this episode" is cut to a set height (see the stylesheet); the
  // "See more" button shows only when the text runs past it.
  useEffect(() => {
    const box = aboutRef.current
    if (!box) return undefined
    const measure = () =>
      setAboutLong(
        box.classList.contains("podcast-episode--about-closed")
          ? box.scrollHeight > box.clientHeight + 1
          : true
      )
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  const playFrom = seconds => {
    setSeek({ seconds })
    document
      .getElementById("episode-player")
      ?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  return (
    <>
      <Seo
        description={{ description: episode.summary }}
        schemaMarkup={schemaFor(episode, path)}
        slug={path.replace(/^\/|\/$/g, "")}
        title={`${episode.title} | Magnificent Minds Podcast`}
      />
      <Header {...header} />

      <section className="podcast-episode--top">
        <div className="container-v2">
          {episode.youtubeLink && (
            <div className="podcast-episode--player" id="episode-player">
              <YoutubeVideo
                seek={seek}
                title={episode.title}
                url={episode.youtubeLink}
              />
            </div>
          )}
          <div className="podcast-episode--heading">
            <h1 className="podcast-episode--title">{episode.title}</h1>
            <p className="podcast-library--details">{details(episode)}</p>
            <TopicTags topics={episode.topics} />
          </div>
          {episode.audioUrl && (
            <div className="podcast-episode--audio">
              <p className="podcast-episode--audio-label">
                Prefer to listen? Play the audio here, or open the episode on
                your favorite app.
              </p>
              <div className="podcast-episode--audio-row">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio
                  aria-label={`Listen to ${episode.title}`}
                  controls
                  preload="none"
                  src={episode.audioUrl}
                />
                <ListenLinks {...episode} />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="podcast-episode--body">
        <div className="container-v2">
          {paragraphs.length > 0 && (
            <div className="podcast-episode--about">
              <h2 className="podcast-episode--section-title">
                About this episode
              </h2>
              {/* The whole description is on the page, cut short until
                  "See more" is clicked. */}
              <div
                className={
                  aboutOpen
                    ? "podcast-episode--about-text"
                    : "podcast-episode--about-text podcast-episode--about-closed"
                }
                id="episode-about"
                ref={aboutRef}
              >
                {paragraphs.map(paragraph => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              {aboutLong && (
                <p className="podcast-episode--about-toggle">
                  <ButtonV2
                    aria-controls="episode-about"
                    aria-expanded={aboutOpen}
                    className={getButtonClasses("Outline Blue", "small")}
                    onClick={() => setAboutOpen(!aboutOpen)}
                    text={aboutOpen ? "See less" : "See more"}
                  />
                </p>
              )}
            </div>
          )}

          {transcript?.length > 0 && (
            <div className="podcast-episode--transcript">
              <h2 className="podcast-episode--section-title">Transcript</h2>
              <p className="podcast-episode--transcript-note">
                Click a time to play the video from that point. This transcript
                comes from the episode&rsquo;s captions and may contain small
                errors.
              </p>
              <div
                className={`podcast-episode--transcript-text${
                  transcriptOpen ? " podcast-episode--transcript-open" : ""
                }`}
                id="episode-transcript"
              >
                {transcript.map(({ start, text }) => (
                  <p key={start}>
                    {episode.youtubeLink ? (
                      <button
                        aria-label={`Play from ${clock(start)}`}
                        className="podcast-episode--time"
                        onClick={() => playFrom(start)}
                        type="button"
                      >
                        {clock(start)}
                      </button>
                    ) : (
                      <span className="podcast-episode--time">
                        {clock(start)}
                      </span>
                    )}
                    {text}
                  </p>
                ))}
              </div>
              <p className="podcast-episode--transcript-toggle">
                <ButtonV2
                  aria-controls="episode-transcript"
                  aria-expanded={transcriptOpen}
                  className={getButtonClasses("Outline Blue", "small")}
                  onClick={() => setTranscriptOpen(!transcriptOpen)}
                  text={
                    transcriptOpen ? "Show less" : "Show the full transcript"
                  }
                />
              </p>
            </div>
          )}

          {more.nodes.length > 0 && (
            <div className="podcast-episode--more">
              <div className="podcast-episode--more-header">
                <h2 className="podcast-episode--section-title">
                  More episodes
                </h2>
                <a className="podcast-episode--all" href="/podcast">
                  Explore all episodes
                </a>
              </div>
              <div className="podcast-library--grid">
                {moreEpisodes(episode, more.nodes).map(other => (
                  <EpisodeCard episode={other} key={other.id} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {getStarted && <SplitSectionV2 {...getStarted} />}
      <Footer {...footer} />
    </>
  )
}

PodcastEpisodeTemplate.propTypes = {
  data: PropTypes.shape({
    episode: PropTypes.object.isRequired,
    footer: PropTypes.object,
    getStarted: PropTypes.object,
    header: PropTypes.object,
    more: PropTypes.shape({ nodes: PropTypes.array }),
  }).isRequired,
  pageContext: PropTypes.shape({
    transcript: PropTypes.arrayOf(
      PropTypes.shape({ start: PropTypes.number, text: PropTypes.string })
    ),
  }),
  path: PropTypes.string,
}

export default PodcastEpisodeTemplate

export const query = graphql`
  query PodcastEpisode($id: String!) {
    header: contentfulGlobalHeader(name: { eq: "Header" }) {
      announcement {
        json
      }
      menu {
        menus {
          label
          links {
            text
            path
            nestedLinks {
              text
              path
            }
          }
        }
      }
      utilityLinks {
        path
        text
      }
      cta {
        href: path
        text
      }
      callsToAction {
        ...ButtonV2Fields
      }
    }
    footer: contentfulGlobalFooter(name: { eq: "Footer" }) {
      menu {
        menus {
          label
          links {
            path
            text
          }
        }
      }
      scheduleAppointment {
        path
        text
      }
      socialLinks {
        path
        text
      }
      utilityLinks {
        path
        text
      }
    }
    episode: podcastEpisode(id: { eq: $id }) {
      applePodcastLink
      audioUrl
      descriptionParagraphs
      durationMinutes
      episode
      publishedOn
      releaseDate: publishedOn(formatString: "MMMM D, YYYY")
      spotifyLink
      summary
      title
      topics
      youtubeLink
    }
    more: allPodcastEpisode(
      filter: { id: { ne: $id } }
      sort: { fields: publishedOn, order: DESC }
    ) {
      nodes {
        id
        applePodcastLink
        durationMinutes
        episode
        releaseDate: publishedOn(formatString: "MMM D, YYYY")
        slug
        spotifyLink
        title
        topics
        youtubeLink
      }
    }
    # The site's closing "Get started" section, the same entry the Whole
    # Child page ends with.
    getStarted: contentfulSectionSplitSectionV2(
      title: { eq: "Early Intervention Split Section" }
    ) {
      callsToAction {
        ...ButtonV2Fields
      }
      content {
        json
      }
      contentOrientation
      heading
      image {
        title
        description
        file {
          url
        }
        fluid(maxWidth: 640, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      subTitle
    }
  }
`
